import { useEffect, useMemo, useState } from 'react';
import { useTimeClockPolicyStore } from '@/store/useTimeClockPolicyStore';
import { stampingWindowKey, useStampingWindowStore } from '@/store/useStampingWindowStore';
import { useAuthStore } from '@/store/useAuthStore';
import {
  DEFAULT_CLOCK_IN_WINDOW,
  formatWindowHour,
  hourIn,
  isWithinClockInWindow,
  isoDateIn,
  type ClockInWindow,
} from '@/utils/dateUtils';

export interface ClockInWindowState {
  /** Whether clocking in is allowed right now; re-evaluated every minute. */
  allowed: boolean;
  /** Window bounds as "HH:00", ready for the `{{start}}`/`{{end}}` hint texts. */
  start: string;
  end: string;
  /** Work time counts from this hour ("HH:00"); a clock-in before it counts only from then. */
  workCountStart: string;
  /** Clocking in is allowed right now, but the time only counts from `workCountStart`. */
  beforeWorkCount: boolean;
  /** Name of the time clock group whose season applies today; null for the tenant policy. */
  groupName: string | null;
}

/**
 * The user's effective clock-in window for the local today (FACF-149): the season of the own
 * time clock group from `/me/timeclock/window`, falling back to the tenant policy (and while
 * loading to the backend default 05–20). A disabled policy never blocks clocking in.
 * Loads again when the local date changes.
 */
export function useClockInWindow(): ClockInWindowState {
  const policy = useTimeClockPolicyStore((s) => s.policy);
  const fetchPolicy = useTimeClockPolicyStore((s) => s.fetchPolicy);
  const fetchWindow = useStampingWindowStore((s) => s.fetchWindow);
  const [now, setNow] = useState(() => new Date());

  const policyTimeZoneId = policy?.timeZoneId ?? null;
  const date = isoDateIn(now, policyTimeZoneId);
  const employeeId = useAuthStore((s) => s.employee?.id ?? null);
  const effective = useStampingWindowStore((s) => s.windows[stampingWindowKey(employeeId, date)]);

  useEffect(() => {
    void fetchPolicy();
  }, [fetchPolicy]);

  // why: also runs on every minute tick, so a failed load is retried once its back-off expired
  // (the store skips the request while cached or within the retry delay).
  useEffect(() => {
    void fetchWindow(employeeId, date);
  }, [fetchWindow, employeeId, date, now]);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    const onFocus = () => setNow(new Date());
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const rule = useMemo<ClockInWindow>(() => {
    const D = DEFAULT_CLOCK_IN_WINDOW;
    const isEnabled = policy?.isEnabled ?? D.isEnabled;
    if (effective) {
      return {
        isEnabled,
        startHour: effective.stampingStartHour ?? D.startHour,
        endHour: effective.stampingEndHour ?? D.endHour,
        workCountStartHour: effective.workCountStartHour ?? D.workCountStartHour,
        timeZoneId: effective.timeZoneId ?? policyTimeZoneId,
      };
    }
    if (policy) {
      return {
        isEnabled,
        startHour: policy.stampingStartHour ?? D.startHour,
        endHour: policy.stampingEndHour ?? D.endHour,
        workCountStartHour: policy.workCountStartHour ?? D.workCountStartHour,
        timeZoneId: policyTimeZoneId,
      };
    }
    return D;
  }, [effective, policy, policyTimeZoneId]);

  const allowed = isWithinClockInWindow(rule, now);
  return {
    allowed,
    start: formatWindowHour(rule.startHour),
    end: formatWindowHour(rule.endHour),
    workCountStart: formatWindowHour(rule.workCountStartHour),
    beforeWorkCount:
      rule.isEnabled && allowed && hourIn(now, rule.timeZoneId) < rule.workCountStartHour,
    groupName: effective?.groupName ?? null,
  };
}
